/* eslint-disable react/sort-comp */
/* eslint-disable react/no-multi-comp */
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import classNames from 'classnames'
import Fuse from 'fuse.js'
import { useCombobox } from 'downshift'
import { Icon, Spinner } from '@scentregroup/ui'

import analyticsUtils from '../../helpers/analytics-utils'
import trackEvent from '../../helpers/analytics-utils/track-event'
import getNearestCentres from '../../helpers/get-nearest-centres'
import { Centre as CentreType } from '../../../types/centre'
import { useRecentCentres } from '../../hooks/use-recent-centres'
import { LinkContext } from '../../context'

export type Link = React.FC<{
  to: string
  title?: string
  className?: string
  onClick?: (event: Record<string, unknown>) => void
}>

export interface Centre extends Partial<CentreType> {
  displayTitle?: string
}

export interface CentreSelectorConfig {
  inputLabel: string
  locationLabel: string
  allCentresLabel: string
}

const defaultCentreSelectorConfig = {
  allCentresLabel: 'All centres',
  inputLabel: 'Find a centre',
  locationLabel: 'Or use your current location',
}

export const MAX_SUGGESTIONS = 3

const trackSearchStarted = (): void => {
  analyticsUtils.gaTagTracking({
    event: analyticsUtils.events.SEARCH,
    eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
    eventAction: analyticsUtils.actions.CLICK_TO_START_SEARCH,
  })
}

const preventDefault: React.ReactEventHandler = e => e.preventDefault()

export const getCentreDisplayTitle = (centre?: Centre): string => {
  if (!centre) {
    return ''
  }

  const location =
    centre.suburb === centre.title
      ? centre.state
      : `${centre.suburb}, ${centre.state}`

  return `${centre.title}, ${location}`
}

export const CentreSelector = withRouter(
  ({
    centres,
    centre: currentCentre,
    navStyle,
    config = defaultCentreSelectorConfig,
    history,
    callBackSetSuggestedCentres,
    onClose,
  }: RouteComponentProps & {
    centres: CentreType[]
    centre?: CentreType
    config?: CentreSelectorConfig
    navStyle: any
    callBackSetSuggestedCentres?: (suggestedCentres: Centre[]) => void
    onClose?: () => void
  }) => {
    const style = navStyle
    const inputRef = useRef<HTMLInputElement>(null)
    const [loadingLocation, setLoadingLocation] = useState(false)
    const [loadingSelectedCentre, setLoadingSelectedCentre] = useState(false)
    const Link = useContext(LinkContext)

    // Inject a `.displayTitle` field for better rendering
    const centresWithDisplayTitle: Centre[] = useMemo(
      () =>
        centres.map(centre => ({
          ...centre,
          displayTitle: getCentreDisplayTitle(centre),
        })),
      [centres]
    )

    const recentCentres: string[] = useRecentCentres()

    const [suggestedCentres, setSuggestedCentres] = useState<Centre[]>(
      (): Centre[] => {
        // Filter out the current centre from suggestions
        const filteredSlugs = currentCentre
          ? recentCentres.filter(slug => slug !== currentCentre.slug)
          : recentCentres

        return (
          filteredSlugs
            // Convert it into a complete object
            .map(slug =>
              centresWithDisplayTitle.find(centre => centre.slug === slug)
            )
            // Clear out any bad data
            .filter(centre => Boolean(centre))
            // Limit the number of suggestions we return
            .slice(0, MAX_SUGGESTIONS) as Centre[]
        )
      }
    )

    const [filteredCentres, setFilteredCentres] = useState(
      centresWithDisplayTitle
    )

    const fuse = useMemo(
      () =>
        new Fuse(centresWithDisplayTitle, {
          shouldSort: true,
          threshold: 0.3,
          location: 0,
          keys: ['displayTitle'],
        }),
      [centresWithDisplayTitle]
    )

    const filterCentres = useCallback(
      ({ inputValue }) => {
        if (inputValue) {
          const foundCentres = fuse.search(inputValue)
          setFilteredCentres(foundCentres)
          analyticsUtils.gaTagTracking({
            event: analyticsUtils.events.SEARCH,
            eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
            eventAction: analyticsUtils.actions.SEARCH_SHOWN,
            eventLabel: inputValue,
            noReturnedResults: foundCentres.length,
          })
        } else {
          setFilteredCentres(centresWithDisplayTitle)
        }
      },
      [fuse, centresWithDisplayTitle]
    )

    const {
      isOpen,
      openMenu,
      getLabelProps,
      getMenuProps,
      getInputProps,
      getComboboxProps,
      highlightedIndex,
      getItemProps,
      inputValue,
    } = useCombobox({
      items: filteredCentres,
      onInputValueChange: filterCentres,
      inputId: 'centreSearch',
      itemToString: item =>
        item && item.displayTitle ? item.displayTitle : '',
      onSelectedItemChange({ selectedItem: centre, type }) {
        // For keyboard users, pressing Enter doesn't trigger the <Link> tag, so
        // we have to do a manual route change
        if (centre && type === useCombobox.stateChangeTypes.InputKeyDownEnter) {
          onCentreSelected(centre)
          onClose?.()
          history.push(`/${centre.slug}`)
        }
      },
      stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges

        // When selecting the "No results" item, we don't want the state to change
        // (ie; we act like the selection never happened)
        if (
          !filteredCentres.length &&
          (type === useCombobox.stateChangeTypes.ItemClick ||
            type === useCombobox.stateChangeTypes.InputKeyDownEnter)
        ) {
          return state
        }

        return changes // otherwise business as usual.
      },
    })

    const allCentres = useCallback(() => {
      setFilteredCentres(centresWithDisplayTitle)
      inputRef.current?.focus()
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_SHOWN,
        eventLabel: '',
        noReturnedResults: centresWithDisplayTitle.length,
      })
    }, [centresWithDisplayTitle])

    const onCentreSelected = useCallback(
      centre => {
        setLoadingSelectedCentre(true)
        analyticsUtils.gaTagTracking({
          event: analyticsUtils.events.SEARCH,
          eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
          eventAction: analyticsUtils.actions.SEARCH_RESULT_ClICKED,
          eventLabel: inputValue,
          centreResultClicked: centre.title,
        })
      },
      [inputValue]
    )

    const getSuggestedCentresNearMe = useCallback(() => {
      setLoadingLocation(true)
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
        eventAction: analyticsUtils.actions.CLICK_TO_SET_LOCATION,
      })

      try {
        // For browsers that do not support geolocation
        navigator.geolocation.getCurrentPosition(
          position => {
            const nearestCentres = getNearestCentres(
              position.coords.latitude,
              position.coords.longitude,
              centresWithDisplayTitle
            ).slice(0, MAX_SUGGESTIONS)
            setSuggestedCentres(nearestCentres)
            if (callBackSetSuggestedCentres) {
              callBackSetSuggestedCentres(nearestCentres)
            }
            setLoadingLocation(false)
          },
          () => {
            setLoadingLocation(false)
          },
          {
            timeout: 10000,
          }
        )
      } catch (e) {
        setLoadingLocation(false)
        // TODO: Verify this is the right event
        analyticsUtils.gaTagTracking({
          event: analyticsUtils.events.SEARCH,
          eventCategory: analyticsUtils.categories.CENTRE_SEARCH,
          eventAction: analyticsUtils.actions.CLICK_TO_SET_LOCATION_DENIED,
        })
      }
    }, [centresWithDisplayTitle])

    return (
      <div className={style['find-a-centre']} data-test-id="centre-selector">
        <form
          action="#"
          /* no-op but makes iOS use 'SEARCH' as return key type */ onSubmit={
            preventDefault
          }
        >
          {/* The label + control are handled by downshift's props */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label {...getLabelProps()}>Centre</label>
          <div {...getComboboxProps()} className={style.combobox}>
            <Icon
              type="search"
              size="small"
              className={style.CentreSearchIcon}
            />
            <input
              {...getInputProps({
                ref: inputRef,
                disabled: loadingSelectedCentre,
                autoCorrect: 'off',
                type: 'search',
                name: 'centreSearch',
                placeholder: config.inputLabel,
                className: style.input,
                onFocus: () => {
                  trackSearchStarted()
                  if (!isOpen) {
                    openMenu()
                  }
                },
              })}
              data-test-id="centre-search"
            />
            {loadingSelectedCentre && (
              <div className={style['spinner-container']}>
                <Spinner />
              </div>
            )}
          </div>
          <ul
            {...getMenuProps()}
            className={classNames({
              [style['filtered-centres']]: isOpen,
            })}
          >
            {isOpen &&
              (filteredCentres.length ? (
                filteredCentres.map((centre, index) => (
                  <li
                    className={classNames(style.centre, {
                      [style['highlighted-centre']]: highlightedIndex === index,
                    })}
                    key={`${centre.displayTitle}${index}`}
                    {...getItemProps({ item: centre, index })}
                  >
                    <Link
                      data-test-id="centre-search-result"
                      to={`/${centre.slug}`}
                      onClick={() => {
                        trackEvent(
                          analyticsUtils.categories.CENTRE_SEARCH,
                          analyticsUtils.actions.CENTRE_SELECTED,
                          centre.displayTitle
                        )
                        onCentreSelected(centre)
                        onClose?.()
                      }}
                    >
                      <span className={style.centreName}>{centre.title}</span>
                      <span className={style.centreState}>{centre.state}</span>
                    </Link>
                  </li>
                ))
              ) : (
                <li
                  className={classNames(style['no-results'], style.centre)}
                  key="no-results"
                  {...getItemProps({
                    item: { displayTitle: 'No results' },
                    index: 0,
                  })}
                >
                  No results
                </li>
              ))}
          </ul>
        </form>
        <button
          className={style['use-location']}
          onClick={getSuggestedCentresNearMe}
        >
          <Icon
            type="location-drop-pin"
            size="small"
            className={style.UseLocationIcon}
          />
          <span>{config.locationLabel}</span>
          {loadingLocation && (
            <div className={style['spinner-container']}>
              <Spinner />
            </div>
          )}
        </button>
        <button className={style['all-centres']} onClick={allCentres}>
          <Icon
            type="list-view"
            size="small"
            className={style.AllCentresIcon}
          />
          <span>{config.allCentresLabel}</span>
        </button>
        {suggestedCentres.length ? (
          <div className={style.suggested}>
            <div className={style.subtitle}>Suggested</div>
            <ul className={style['suggested-items']}>
              {suggestedCentres.map(centre => (
                <li key={centre.slug}>
                  <Link
                    to={`/${centre.slug}`}
                    className={style['suggested-item']}
                    onClick={onClose}
                  >
                    {centre.displayTitle}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    )
  }
)
